import { useMemo } from "react";
import { useOutletContext } from "react-router";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { withSkeleton, dateBody, skeletonRows } from "@/helpers/tableBody";
import type { UserData } from "@/pages/Users";
import type { Lesson } from "@/api/users";

const lessonNameBody = withSkeleton<Lesson>(row => row.name ?? "--", "20rem");
const completedAtBody = dateBody<Lesson>("completedAt", "N/A");
const resultBody = withSkeleton<Lesson>(row => (row.hackedu ? "N/A" : (row.result ?? "N/A")));
const passedBody = withSkeleton<Lesson>(row => {
  if (row.passed == null) return "N/A";
  return (
    <i
      className={`pi ${row.passed ? "pi-check-circle" : "pi-times-circle text-warning"}`}
      aria-label={row.passed ? "Passed" : "Failed"}
    />
  );
});

export default function UserHistoryTab() {
  const { user, isLoading } = useOutletContext<{ user: UserData | null; isLoading: boolean }>();
  const skeletonData = useMemo(() => skeletonRows<Lesson>(10), []);

  const emptyMessage =
    !isLoading && !user?.lessons
      ? "Lesson history is not available for this user. This data requires additional context."
      : "No lesson history available.";

  return (
    <div>
      <h2 className="atlas-h2 mb-4">Lesson Activity History</h2>
      <p className="body-sm mb-4">
        A record of all lessons completed by this user on the platform.
      </p>
      <DataTable<Lesson[]>
        value={(isLoading ? skeletonData : (user?.lessons ?? [])) as unknown as Lesson[]}
        loading={false}
        paginator
        aria-label="User lesson history"
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage={emptyMessage}
        tableClassName="dt-responsive-text"
      >
        <Column
          sortable
          field="name"
          header="Lesson Name"
          body={lessonNameBody}
          style={{ width: "500px" }}
        />
        <Column sortable field="completedAt" header="Date Completed" body={completedAtBody} />
        <Column sortable field="result" header="Last Result" body={resultBody} />
        <Column sortable field="passed" header="Passed" body={passedBody} />
      </DataTable>
    </div>
  );
}
